import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { DateTimeFormatter, LocalDateTime } from "@js-joda/core";
import { useRenderNumber } from "../../../api/utilities/useValueRenderers.js";
import Button from "../../../components/Button.jsx";

const DomainsConfirmationModal = ({
    isOpen,
    onConfirm,
    onClose,
    additionalPrice,
    fullPrice,
    domainsToPay,
    currencyCode,
    project
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const renderNumber = useRenderNumber();

    if (!isOpen) return null;

    const subscription = project.subscription || {};
    const subscriptionDomainsCount = subscription.currentPackage?.maxDomains ?? 1;
    const extraPaidDomainsCount = subscription.extraPaidDomainsCount ?? 0;
    const endDateString = subscription.endDate
        ? LocalDateTime.parse(subscription.endDate).format(DateTimeFormatter.ofPattern('dd.MM.yyyy'))
        : null;

    const formattedPrice = renderNumber(additionalPrice / 100);
    const formattedFullPrice = renderNumber(fullPrice / 100);

    const handleUpgradePlan = () => {
        navigate(`/project/${project.id}/settings/subscription`);
        onClose();
    };

    return ReactDOM.createPortal(
        <div className="modal__domains fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex">
            <div className="relative bg-white dark:bg-gray-800 w-full max-w-lg m-auto flex-col flex rounded-lg shadow-lg">
                <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700/60">
                    <div className="flex justify-between items-center">
                        <div className="font-semibold text-lg text-gray-800 dark:text-gray-100">
                            {t('domains.confirm-title')}
                        </div>
                        <button
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                            onClick={onClose}
                        >
                            <div className="sr-only">Close</div>
                            <svg className="fill-current" width="16" height="16" viewBox="0 0 16 16">
                                <path
                                    d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z"
                                ></path>
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="px-5 py-4">
                    <div className="text-sm text-gray-600 mb-2">
                        <div className="mb-3 text-violet-600">
                            <strong>{t('domains.exceeded-domains-count')}</strong>
                        </div>

                        <div className="mb-4">
                            {t('domains.additional-domains-available')}
                            <ul className="list-disc pl-3.5 mt-1">
                                <li>{t('domains.first-extra-domain')}</li>
                                <li>{t('domains.other-extra-domain')}</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 py-3.5 px-5 rounded-xl border-1 border-gray-400">
                        <div className="text-black dark:text-gray-100 font-bold mb-2">
                            {t('domains.title')}
                        </div>
                        <ul>
                            <li className="text-sm w-full flex justify-between py-1.5 border-b border-gray-200 dark:border-gray-700/60">
                                <div>{t('domains.included')}</div>
                                <div className="font-medium text-gray-800 dark:text-gray-100">{subscriptionDomainsCount}</div>
                            </li>
                            {extraPaidDomainsCount > 0 && (
                                <li className="text-sm w-full flex justify-between py-1.5 border-b border-gray-200 dark:border-gray-700/60">
                                    <div>{t('domains.extra-paid')}</div>
                                    <div className="font-medium text-gray-800 dark:text-gray-100">{extraPaidDomainsCount}</div>
                                </li>
                            )}
                            <li className="text-sm w-full flex justify-between pt-1.5">
                                <div>{t('domains.to-pay')}</div>
                                <div className="font-medium text-gray-800 dark:text-gray-100">{domainsToPay}</div>
                            </li>
                        </ul>
                    </div>

                    <div
                        className="mt-3 mb-3 w-full h-full text-left py-3 px-4 rounded-lg bg-white dark:bg-gray-800 border border-violet-400 dark:border-violet-500 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between mb-0.5">
                            <div className="text-black font-bold text-gray-800 dark:text-gray-100">
                                <div>{t('domains.total-to-pay')} <span className={'font-normal text-sm text-violet-500'}>({domainsToPay})</span></div>
                            </div>
                            <div className="font-medium text-green-600 text-right">
                                <small className={'text-red-700 line-through'}>{formattedFullPrice} Kč</small>
                                <div>{formattedPrice} Kč</div>
                            </div>
                        </div>
                    </div>
                    <div className="text-xs text-gray-500">
                        {t('domains.price-description', { endDateString })}
                    </div>
                </div>

                <div className="px-2.5 py-2 flex gap-1 border-t border-gray-200 dark:border-gray-700/60">
                    <Button variant="outline-red" onClick={handleUpgradePlan}>
                        {t('domains.change-tarif')}
                    </Button>
                    <Button variant="black" className="ml-auto">
                        {t('domains.pay')}
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DomainsConfirmationModal;
